import { Select } from 'src/design/atoms/Select';
import React from 'react';
import { JobSummaryDto } from 'src/api/FiretrolApiClient';
import { useCtcJobOptions } from 'src/api/queries/ctc/useCtcJobOptions';
import { useLinkedCtcJobs } from 'src/api/queries/ctc/useLinkedCtcJobs';

const NO_JOBS: JobSummaryDto[] = [];

export interface ICTCJobSelectProps {
  onChange: (value: JobSummaryDto | null) => void;
  hideLabel?: boolean;
  small?: boolean;
  ctcJobId: string;
  disabled?: boolean;
  placeholder?: string;
}

/** @registry Single-select searchable dropdown of Great Plains (CTC) jobs, for linking a Firetrol job to its CTC counterpart. */
export const CTCJobSelector: React.FC<ICTCJobSelectProps> = ({
  onChange,
  placeholder = 'Select a CTC Job...',
  disabled = false,
  ctcJobId,
  small = false,
  hideLabel = false,
}) => {
  const ctcJobsQuery = useCtcJobOptions();
  const linkedJobsQuery = useLinkedCtcJobs();
  const loading = ctcJobsQuery.isLoading || linkedJobsQuery.isLoading;

  // Only jobs that are not already linked to a Firetrol job can be picked.
  const jobs = React.useMemo(() => {
    const all = ctcJobsQuery.data?.jobs;
    const linked = linkedJobsQuery.data;
    if (!all || !linked) return NO_JOBS;
    return all.filter((j) => linked.indexOf(j.wsJobNumber) === -1);
  }, [ctcJobsQuery.data, linkedJobsQuery.data]);

  const selectedJob = React.useMemo(
    () =>
      ctcJobId ? jobs.find((j) => j.wsJobNumber === ctcJobId) ?? null : null,
    [jobs, ctcJobId],
  );

  const data = React.useMemo(() => {
    return jobs.map((job) => ({
      value: job.wsJobNumber,
      label: `${job.wsJobNumber} - ${job.wsJobName}`,
    }));
  }, [jobs]);

  const selected = selectedJob ? selectedJob.wsJobNumber : null;

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedJob = jobs.find((job) => job.wsJobNumber === selectedValue);
      onChange(selectedJob || null);
    } else {
      onChange(null);
    }
  };

  return (
    <Select
      label={!hideLabel ? 'CTC Job' : undefined}
      placeholder={placeholder}
      data={data}
      value={selected}
      onChange={handleChange}
      searchable
      clearable
      disabled={disabled || loading}
      className={small ? 'single-small' : ''}
    />
  );
};
